import { ChangeEvent, useRef, useState } from "react";

import { CheckIcon, CloseIcon, EditIcon } from "@/assets/icons";
import useOnClickOutside from "@/hooks/ui/useClickOutside";

import { twMerge } from "tailwind-merge";

const TEST_ID_PREFIX = "edit-field";

type Props = {
  inputFieldName?: string;
  value: string;
  className?: string;
  textClassName?: string;
  iconClassName?: string;
  placeHolder?: string;
  valuePlaceholder?: string;
  disabled?: boolean;
  onChange?: (text: string) => void;
  onClickCheckIcon?: (text: string) => void;
  // When set, the edit button calls this instead of entering edit mode
  onEditBlocked?: () => void;
  testId?: string;
};

const EditField = ({
  inputFieldName = "edit-field-input",
  value,
  className,
  textClassName,
  iconClassName,
  placeHolder = "",
  valuePlaceholder = "",
  disabled = false,
  onChange,
  onClickCheckIcon,
  onEditBlocked,
  testId = TEST_ID_PREFIX
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [text, setText] = useState<string>(value);

  useOnClickOutside(ref, () => handleCancel(), []);

  const handleChangeText = (e: ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
  };

  const handleCancel = () => {
    setIsEdit(false);
    setText(value);
  };

  const handleSave = () => {
    setIsEdit(false);
    onChange?.(text);
    onClickCheckIcon?.(text);
  };

  if (value !== text && !isEdit) {
    setText(value);
  }

  return (
    <div className={twMerge("px-4", className)} ref={ref}>
      {isEdit ? (
        <div className="flex gap-2">
          <input
            id={inputFieldName}
            name={inputFieldName}
            className={twMerge(
              "px-1 focus:outline-none bg-blue-50 focus:borderfocus:border-gainsboro text-txtHeading rounded-[4px] w-full",
              textClassName
            )}
            value={text}
            placeholder={placeHolder}
            onChange={handleChangeText}
            data-testid={`${testId}-input`}
          />
          <div className="flex gap-2">
            <button
              className="bg-transparent border-0 p-0"
              onClick={handleCancel}
              data-testid={`${testId}-close-button`}
              aria-describedby={inputFieldName}
              aria-label={"Close"}
            >
              <CloseIcon
                className={twMerge("w-4 fill-dim-gray", iconClassName)}
              />
            </button>
            <button
              className="bg-transparent border-0 p-0"
              onClick={handleSave}
              data-testid={`${testId}-save-button`}
              aria-describedby={inputFieldName}
              aria-label={"Save"}
            >
              <CheckIcon
                className={twMerge("w-4 fill-dim-gray", iconClassName)}
              />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span
            id={inputFieldName}
            className={twMerge("truncate", textClassName)}
          >
            {value || valuePlaceholder}
          </span>
          <div className="flex gap-2">
            <button
              className={twMerge(
                "bg-transparent border-0 p-0",
                disabled && "hidden" // hide edit button if disabled
              )}
              onClick={onEditBlocked ?? (() => setIsEdit(!isEdit))}
              data-testid={`${testId}-edit-button`}
              aria-describedby={inputFieldName}
              aria-label={"Edit"}
            >
              <EditIcon
                className={twMerge("w-4 fill-dim-gray", iconClassName)}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditField;
